/**
 * Product-page Cash on Delivery button.
 *
 * Supports, in order:
 * 1. A populated Electra Bundle & Save block (.elx-bundle-save)
 * 2. Kaching Bundles (<kaching-bundles-block>)
 * 3. The standard Shopify product form
 */
(function () {
  const DEFAULT_LABEL = 'Cash on Delivery';
  const LOADING_LABEL = 'Loading...';

  function parseJson(text) {
    try {
      return JSON.parse(text);
    } catch (error) {
      return null;
    }
  }

  function getSearchScope(button) {
    return button.closest('product-info') || document;
  }

  function getQuantityFromForm(form) {
    if (!form) return 1;

    const qtyInput = form.querySelector(
      '[name="quantity"], input.quantity__input'
    );

    const qty = qtyInput ? Number(qtyInput.value) : 1;

    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  /**
   * Electra custom Bundle & Save block.
   */
  function getItemsFromElxBundle(scope) {
    const root = scope.querySelector(
      '[data-elx-bundle-save], .elx-bundle-save'
    );

    if (!root) return null;

    /*
     * Some product pages contain an empty Electra placeholder while
     * the visible bundle interface is supplied by Kaching.
     */
    const options = root.querySelectorAll(
      '.elx-bundle-option'
    );

    if (!options.length) return null;

    let active = root.querySelector(
      '.elx-bundle-option.elx-active, ' +
        '.elx-bundle-option.is-active, ' +
        '.elx-bundle-option.selected, ' +
        '.elx-bundle-option[aria-checked="true"]'
    );

    if (!active) {
      const checkedInput = root.querySelector(
        '.elx-bundle-option input[type="radio"]:checked, ' +
          '.elx-bundle-option input[type="checkbox"]:checked'
      );

      active =
        checkedInput &&
        checkedInput.closest('.elx-bundle-option');
    }

    if (!active) {
      throw new Error(
        'Please select a bundle option.'
      );
    }

    const variantHolder = active.matches(
      '[data-variant-id]'
    )
      ? active
      : active.querySelector('[data-variant-id]');

    const checkedInput = active.querySelector(
      'input[name="id"]:checked, input[type="radio"]:checked'
    );

    const variantId = Number(
      (variantHolder &&
        variantHolder.getAttribute('data-variant-id')) ||
        (checkedInput && checkedInput.value)
    );

    if (!variantId) {
      throw new Error(
        'Selected bundle is missing a variant id.'
      );
    }

    return [
      {
        id: variantId,
        quantity: 1
      }
    ];
  }

  /**
   * Get all products known to the Kaching block.
   */
  function getKachingProducts(kachingBlock) {
    const attributes = [
      'product',
      'collection-breaks-products',
      'other-products'
    ];

    const products = [];

    attributes.forEach(function (attribute) {
      const value = parseJson(
        kachingBlock.getAttribute(attribute)
      );

      if (Array.isArray(value)) {
        products.push.apply(products, value);
      } else if (value) {
        products.push(value);
      }
    });

    return products.filter(function (
      product,
      index,
      allProducts
    ) {
      return (
        product &&
        product.id &&
        allProducts.findIndex(function (candidate) {
          return String(candidate.id) === String(product.id);
        }) === index
      );
    });
  }

  /**
   * Get the configured quantity for the selected Kaching deal.
   */
  function getKachingBundleQuantity(
    kachingBlock,
    selectedBar
  ) {
    const dealBlock = parseJson(
      kachingBlock.getAttribute('deal-block')
    );

    const selectedDealBarId =
      selectedBar.getAttribute('data-deal-bar-id');

    if (
      dealBlock &&
      Array.isArray(dealBlock.dealBars) &&
      selectedDealBarId
    ) {
      const selectedDeal = dealBlock.dealBars.find(
        function (deal) {
          return (
            String(deal.id) === String(selectedDealBarId)
          );
        }
      );

      const configuredQuantity =
        selectedDeal &&
        Number(selectedDeal.quantity);

      if (
        Number.isFinite(configuredQuantity) &&
        configuredQuantity > 0
      ) {
        return configuredQuantity;
      }
    }

    const title =
      selectedBar.querySelector(
        '.kaching-bundles__bar-title'
      )?.textContent || '';

    const quantityMatch = title.match(/\d+/);

    return quantityMatch
      ? Number(quantityMatch[0])
      : 1;
  }

  /**
   * Find the product represented by one Kaching bundle row.
   */
  function getKachingRowProduct(
    row,
    products,
    fallbackProduct
  ) {
    const title = row
      .querySelector(
        '.kaching-bundles__collection-product__title'
      )
      ?.textContent.trim();

    if (!title) return fallbackProduct;

    return (
      products.find(function (product) {
        return product.title === title;
      }) || fallbackProduct
    );
  }

  /**
   * Read Kaching's selected variant option values.
   */
  function getKachingOptionValues(row, product) {
    const valuesByName = {};

    row
      .querySelectorAll(
        '.kaching-bundles-option[data-value]'
      )
      .forEach(function (option) {
        const name =
          option.getAttribute('data-name');

        const value =
          option.getAttribute('data-value');

        if (name && value) {
          valuesByName[name] = value;
        }
      });

    const nativeValues = Array.from(
      row.querySelectorAll(
        'select.kaching-bundles__bar-variant-select'
      )
    ).map(function (select) {
      return select.value;
    });

    const productOptions =
      Array.isArray(product.options)
        ? product.options
        : [];

    if (!productOptions.length) {
      return nativeValues.filter(Boolean);
    }

    return productOptions.map(function (
      option,
      index
    ) {
      const name =
        typeof option === 'string'
          ? option
          : option.name;

      return (
        valuesByName[name] ||
        nativeValues[index] ||
        null
      );
    });
  }

  /**
   * Resolve one Kaching bundle row to a Shopify variant.
   */
  function resolveKachingRowVariant(
    row,
    products,
    fallbackProduct
  ) {
    const hasChooseButton = Boolean(
      row.querySelector(
        '.kaching-bundles__collection-product__choose-product-button'
      )
    );

    const onlyAvailableProduct =
      products.length === 1
        ? products[0]
        : null;

    /*
     * Kaching can show a "Choose" placeholder even when only
     * one product and one variant are possible. Resolve that
     * automatically.
     */
    if (
      hasChooseButton &&
      !(
        onlyAvailableProduct &&
        Array.isArray(
          onlyAvailableProduct.variants
        ) &&
        onlyAvailableProduct.variants.length === 1
      )
    ) {
      throw new Error(
        'Please choose all products in your bundle.'
      );
    }

    const product = hasChooseButton
      ? onlyAvailableProduct
      : getKachingRowProduct(
          row,
          products,
          fallbackProduct
        );

    if (
      !product ||
      !Array.isArray(product.variants)
    ) {
      throw new Error(
        'Bundle product data is missing.'
      );
    }

    let variant = null;

    /*
     * The Memory Foam Cushion has an option named "Color,"
     * but Black is its only variant. Kaching therefore renders
     * no colour selector.
     */
    if (product.variants.length === 1) {
      variant = product.variants[0];
    } else {
      const optionValues =
        getKachingOptionValues(row, product);

      if (
        optionValues.some(function (value) {
          return !value;
        })
      ) {
        throw new Error(
          'Please select all bundle variants.'
        );
      }

      variant = product.variants.find(
        function (candidate) {
          return (
            Array.isArray(candidate.options) &&
            candidate.options.length ===
              optionValues.length &&
            candidate.options.every(
              function (value, index) {
                return value === optionValues[index];
              }
            )
          );
        }
      );
    }

    if (!variant || !variant.id) {
      throw new Error(
        'A selected bundle variant could not be found.'
      );
    }

    if (variant.availableForSale === false) {
      throw new Error(
        'A selected bundle variant is sold out.'
      );
    }

    return {
      id: Number(variant.id),
      quantity: 1
    };
  }

  /**
   * Kaching Bundles app block.
   */
  function getItemsFromKaching(scope) {
    const kachingBlock = scope.querySelector(
      'kaching-bundles-block'
    );

    if (!kachingBlock) return null;

    const products =
      getKachingProducts(kachingBlock);

    const fallbackProduct = parseJson(
      kachingBlock.getAttribute('product')
    );

    if (!fallbackProduct) {
      throw new Error(
        'Product data is missing.'
      );
    }

    const selectedBar = kachingBlock.querySelector(
      '.kaching-bundles__bar--selected'
    );

    if (!selectedBar) {
      throw new Error(
        'Please select a bundle.'
      );
    }

    const productRows = Array.from(
      selectedBar.querySelectorAll(
        '.kaching-bundles__collection-product'
      )
    );

    if (productRows.length) {
      const rowItems = productRows.map(
        function (row) {
          return resolveKachingRowVariant(
            row,
            products,
            fallbackProduct
          );
        }
      );

      return mergeItems(rowItems);
    }

    const bundleQuantity =
      getKachingBundleQuantity(
        kachingBlock,
        selectedBar
      );

    const selectedVariantId = Number(
      kachingBlock.getAttribute(
        'current-variant-id'
      ) ||
        fallbackProduct.selectedVariantId
    );

    if (!selectedVariantId) {
      throw new Error(
        'Selected product variant is missing.'
      );
    }

    return [
      {
        id: selectedVariantId,
        quantity: bundleQuantity
      }
    ];
  }

  /**
   * Standard Shopify product form.
   */
  function getItemsFromProductForm(button) {
    const form =
      button.closest(
        'form[data-type="add-to-cart-form"]'
      ) ||
      button.closest('form.form') ||
      getSearchScope(button).querySelector(
        'form[data-type="add-to-cart-form"]'
      );

    if (!form) return null;

    const variantInput = form.querySelector(
      'input[name="id"].product-variant-id, input[name="id"]'
    );

    if (
      !variantInput ||
      !variantInput.value
    ) {
      return null;
    }

    return [
      {
        id: Number(variantInput.value),
        quantity: getQuantityFromForm(form)
      }
    ];
  }

  /**
   * Merge duplicate variants and add their quantities.
   */
  function mergeItems(rawItems) {
    const merged = {};

    (rawItems || []).forEach(function (item) {
      if (!item || !item.id) return;

      if (!merged[item.id]) {
        merged[item.id] = {
          id: item.id,
          quantity: 0
        };
      }

      merged[item.id].quantity +=
        item.quantity || 0;
    });

    return Object.values(merged).filter(
      function (item) {
        return (
          item.id &&
          item.quantity > 0
        );
      }
    );
  }

  /**
   * Determine which products should be added.
   */
  function resolveCartItems(button) {
    const scope = getSearchScope(button);

    const elxItems =
      getItemsFromElxBundle(scope);

    if (
      elxItems &&
      elxItems.length
    ) {
      return elxItems;
    }

    const kachingItems =
      getItemsFromKaching(scope);

    if (
      kachingItems &&
      kachingItems.length
    ) {
      return kachingItems;
    }

    const formItems =
      getItemsFromProductForm(button);

    if (
      formItems &&
      formItems.length
    ) {
      return formItems;
    }

    throw new Error(
      'Could not determine the selected product. Please select a bundle or variant.'
    );
  }

  /**
   * Add the resolved products to Shopify's cart.
   */
  async function addToCart(items) {
    const root =
      window.Shopify?.routes?.root || '/';

    const addResponse = await fetch(
      root + 'cart/add.js',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: items
        })
      }
    );

    if (!addResponse.ok) {
      const errorBody = parseJson(
        await addResponse.text()
      );

      throw new Error(
        errorBody?.description ||
          errorBody?.message ||
          'Failed to add items to cart.'
      );
    }
  }

  /**
   * Update the Cash on Delivery button label and state.
   */
  function setButtonState(button, loading) {
    const normalLabel =
      button.dataset.codLabel ||
      DEFAULT_LABEL;

    const loadingLabel =
      button.dataset.codLoadingLabel ||
      LOADING_LABEL;

    button.disabled = loading;

    button.textContent = loading
      ? loadingLabel
      : normalLabel;
  }

  /**
   * Handle a Cash on Delivery click.
   */
  async function handleCodClick(button) {
    setButtonState(button, true);

    try {
      const items =
        resolveCartItems(button);

      if (!items.length) {
        throw new Error(
          'Could not determine the selected variants.'
        );
      }

      await addToCart(items);

      const root =
        window.Shopify?.routes?.root || '/';

      window.location.assign(
        root + 'checkout'
      );
    } catch (error) {
      console.error(
        '[COD button]',
        error
      );

      alert(
        error.message ||
          'Something went wrong. Please try again.'
      );

      setButtonState(button, false);
    }
  }

  /**
   * Attach the click event once.
   */
  function bindButton(button) {
    if (
      !button ||
      button.dataset.codBound === 'true'
    ) {
      return;
    }

    button.dataset.codBound = 'true';

    window.addEventListener(
      'pageshow',
      function () {
        setButtonState(button, false);
      }
    );

    button.addEventListener(
      'click',
      function () {
        handleCodClick(button);
      }
    );
  }

  /**
   * Find and initialize every COD button on the page.
   */
  function init() {
    document
      .querySelectorAll(
        '[data-cod-product-button], [id^="CashOnDeliveryButton-"]'
      )
      .forEach(bindButton);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      init
    );
  } else {
    init();
  }
})();